"use client";

import { TypeHero } from "@contentful/types";
import { customImageLoader } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import { H1 } from "@yardzen/ui/components/h1";
import { H3 } from "@yardzen/ui/components/h3";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import Image from "next/image";
import { ContractorFormModal } from "./ContractorFormModal";
import { RichText } from "./rich-text/RichText";
import { StarRating } from "./StarRating";

export function HeroShowcaseBackground({
  hero,
}: {
  hero: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const mobileImage = hero?.fields.mobileOnlyImages?.at(0);
  const marketingImage = hero?.fields.displayOptions?.includes("suppress-title")
    ? "yes"
    : "no";
  // `textSize` gained `small` and `x-large` for the split variants. This
  // variant only ever understood two values, and widening it here would be a
  // silent restyle of every showcase-background hero, so anything outside its
  // pair resolves the way an unset field always did.
  const textSizeResolved =
    hero?.fields.textSize === "medium" ? "medium" : "large";

  const heroVariants = cva("text-brand-beige-lighter", {
    variants: {
      textColor: {
        white: "text-brand-beige-lighter",
        black: "text-brand-primary-darker",
        default: "text-brand-beige-lighter",
      },
      noBgOpacity: {
        yes: "",
        no: "bg-black/50",
      },
      maxHeight: {
        yes: "max-h-[450px]",
        no: "min-h-[480px]",
      },
      textSize: {
        large: "",
        medium: "text-3xl lg:text-5xl",
      },
    },
  });

  return (
    <section
      className={cn(
        "hero-showcase relative",
        heroVariants({ maxHeight: marketingImage }),
      )}
    >
      {hero.fields.customStyles && (
        <style
          // NOTE: customStyles is expected to be valid CSS text coming from Contentful.
          // Author it with selectors scoped to `.hero-showcase` to avoid global leaks.
          dangerouslySetInnerHTML={{ __html: hero.fields.customStyles }}
        />
      )}

      <div
        className={cn(
          "hero-showcase__inner flex h-[92vh]",
          heroVariants({ maxHeight: marketingImage }),
        )}
      >
        <div
          className={cn(
            "hero-showcase__overlay absolute inset-0 z-20",
            heroVariants({ noBgOpacity: marketingImage }),
          )}
        />

        <Image
          src={`https:${hero?.fields.images?.at(0)?.fields.image?.fields.file?.url}`}
          alt={
            hero?.fields.images?.at(0)?.fields.image?.fields.description ?? ""
          }
          fill={true}
          className={cn(
            "hero-showcase__image hero-showcase__image--desktop absolute object-cover sm:!relative sm:!h-auto",
            mobileImage ? "hidden md:block" : "",
          )}
          loader={customImageLoader}
        />

        {mobileImage && (
          <Image
            src={`https:${mobileImage?.fields.image?.fields.file?.url}`}
            alt={mobileImage?.fields.image?.fields.description ?? ""}
            fill={true}
            className={cn(
              "hero-showcase__image hero-showcase__image--mobile absolute object-cover sm:relative",
              mobileImage ? "block md:hidden" : "",
            )}
            loader={customImageLoader}
          />
        )}

        <div
          className={cn(
            "hero-showcase__content-region absolute inset-0 z-30 flex items-center justify-center",
            heroVariants({ maxHeight: marketingImage }),
          )}
        >
          <div className="hero-showcase__content-wrapper flex max-w-[940px] flex-col items-center gap-4 p-6">
            {hero.fields.starRating && (
              <div className="hero-showcase__rating">
                <StarRating
                  starRating={hero.fields.starRating}
                  variant="center"
                />
              </div>
            )}

            {hero.fields.eyebrow && (
              <H3
                className={cn(
                  "hero-showcase__eyebrow max-w-3xl text-center text-lg normal-case",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
              >
                {hero.fields.eyebrow}
              </H3>
            )}

            {hero.fields.title && marketingImage === "no" && (
              <div className="hero-showcase__title-wrapper w-100 center-title text-center">
                <H1
                  className={cn(
                    "hero-showcase__title max-w-3xl text-center normal-case lg:text-6xl",
                    heroVariants({
                      textColor: hero?.fields.textColor,
                      textSize: textSizeResolved,
                    }),
                  )}
                >
                  {hero.fields.title}
                </H1>
              </div>
            )}

            {hero.fields.subtitle && (
              <P
                noSpacing
                className={cn(
                  "hero-showcase__subtitle max-w-2xl text-center text-xl leading-[140%]",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
              >
                {hero.fields.subtitle}
              </P>
            )}

            {hero.fields.description && (
              <P
                noSpacing
                className={cn(
                  "hero-showcase__description max-w-2xl text-center text-base leading-[140%]",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
              >
                {hero.fields.description}
              </P>
            )}

            {hero.fields.CTAs && hero.fields.CTAs.length > 0 ? (
              <div className="hero-showcase__cta-wrapper flex flex-col gap-3 sm:flex-row">
                {hero.fields.CTAs.map((cta) => {
                  if (!cta) return null;
                  return (
                    <Button
                      key={cta.sys.id}
                      variant={cta.fields.variant ?? "default"}
                      className="hero-showcase__cta-button w-full sm:w-auto"
                      asChild
                    >
                      <Link
                        className="hero-showcase__cta-link whitespace-nowrap py-3 font-semibold"
                        href={cta.fields.url}
                        target={cta.fields.openInNewTab ? "_blank" : undefined}
                        rel={
                          cta.fields.openInNewTab
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        {cta.fields.buttonText}
                      </Link>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <>
                {hero.fields.cta && !hero.fields.cta.fields.openInModal && (
                  <div className="hero-showcase__cta-wrapper">
                    <Button
                      variant={hero.fields.cta.fields.variant ?? "default"}
                      className="hero-showcase__cta-button w-full sm:w-auto"
                      asChild
                    >
                      <Link
                        className="hero-showcase__cta-link whitespace-nowrap py-3 font-semibold"
                        href={hero.fields.cta.fields.url}
                        target={
                          hero.fields.cta.fields.openInNewTab
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          hero.fields.cta.fields.openInNewTab
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        {hero.fields.cta?.fields.buttonText}
                      </Link>
                    </Button>
                  </div>
                )}

                {hero.fields.cta?.fields.openInModal === true && (
                  <ContractorFormModal
                    key={hero.fields.cta.sys.id}
                    contentfulCTA={hero.fields.cta}
                  />
                )}
              </>
            )}

            {hero.fields.bottomTextLink && (
              <RichText
                content={hero.fields.bottomTextLink}
                className={cn(
                  "hero-showcase__bottom-text-link max-w-2xl text-center text-base leading-[140%]",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
                pNoSpacing
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
